"use client";
import {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Timestamp } from "firebase/firestore";
import { useNullableUserContext } from "@/app/_hooks/useUserContext";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import {
  claimTimerReminder,
  discardTimer,
  getWorkedMs,
  pauseTimer,
  resumeTimer,
  startTimer,
  stopTimerAndLog,
  subscribeToActiveTimers,
} from "@/app/_firebase/timeEntries";
import { ActiveTimer, ActiveTimersMap, TimeEntry } from "@/app/@types/timeEntry";

const REMINDER_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const LONG_RUNNING_MS = 12 * 60 * 60 * 1000; // 12 hours
const LONG_PAUSED_MS = 2 * 60 * 60 * 1000; // 2 hours

export { getWorkedMs };

export const formatElapsed = (ms: number): string => {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

interface PendingStart {
  jobUid: string;
  jobId: number;
}

interface TimeClockContextType {
  allTimers: ActiveTimersMap;
  // The current team member's running timer, if any.
  myTimer: ActiveTimer | null;
  isLogModalOpen: boolean;
  openLogModal: () => void;
  closeLogModal: () => void;
  // Job the user asked to start timing while another timer was running,
  // started automatically once the current timer is logged or discarded.
  pendingStart: PendingStart | null;
  // Start timing a job; if a timer is already running, opens the log modal
  // first and remembers the requested job (unless it's the same job, then
  // it's just a stop request).
  requestStart: (jobUid: string, jobId: number) => void;
  stopAndLog: (data: Omit<TimeEntry, "uid" | "createdAt">) => Promise<void>;
  discard: () => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
}

const TimeClockContext = createContext<TimeClockContextType | undefined>(
  undefined
);

export const TimeClockProvider = ({ children }: { children: ReactNode }) => {
  const user = useNullableUserContext();
  const setAlert = useSetAlert();
  const queryClient = useQueryClient();
  const [allTimers, setAllTimers] = useState<ActiveTimersMap>({});
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState<PendingStart | null>(null);
  // Session-scoped so the "running over 12 hours" / "paused over 2 hours"
  // prompts fire once, not every minute.
  const longRunningPromptedRef = useRef(false);
  const longPausedPromptedRef = useRef(false);

  const userId = user?.uid;
  const memberUid = user?.details?.uid;

  useEffect(() => {
    if (!userId) return;
    const unsubscribe = subscribeToActiveTimers(userId, setAllTimers);
    return () => unsubscribe();
  }, [userId]);

  // A partial map entry (e.g. only lastNotifiedAt surviving a race with a
  // stop) isn't a running timer.
  const myTimer = useMemo(() => {
    if (!memberUid) return null;
    const timer = allTimers[memberUid];
    return timer?.startedAt ? timer : null;
  }, [allTimers, memberUid]);

  const openLogModal = useCallback(() => setIsLogModalOpen(true), []);
  const closeLogModal = useCallback(() => {
    setIsLogModalOpen(false);
    setPendingStart(null);
  }, []);

  const beginTimer = useCallback(
    async (jobUid: string, jobId: number) => {
      if (!userId || !user?.details) return;
      // Ask once, inside the user gesture (Safari requires it). Denied is
      // fine, reminders fall back to the in-app toast.
      if (
        typeof Notification !== "undefined" &&
        Notification.permission === "default"
      ) {
        Notification.requestPermission().catch(() => {});
      }
      await startTimer(userId, user.details, jobUid, jobId);
      setAlert({
        type: "success",
        title: "Timer started",
        content: `Now tracking time on Job #${jobId}.`,
      });
    },
    [userId, user?.details, setAlert]
  );

  const requestStart = useCallback(
    (jobUid: string, jobId: number) => {
      if (!myTimer) {
        beginTimer(jobUid, jobId);
        return;
      }
      if (myTimer.jobUid !== jobUid) {
        // One timer per person: log (or discard) the running one first, then
        // this job starts automatically.
        setPendingStart({ jobUid, jobId });
      }
      setIsLogModalOpen(true);
    },
    [myTimer, beginTimer]
  );

  const stopAndLog = useCallback(
    async (data: Omit<TimeEntry, "uid" | "createdAt">) => {
      if (!userId || !memberUid) return;
      await stopTimerAndLog(userId, memberUid, data);
      queryClient.invalidateQueries({ queryKey: [userId, "timeEntries"] });
      longRunningPromptedRef.current = false;
      setIsLogModalOpen(false);
      const next = pendingStart;
      setPendingStart(null);
      if (next) await beginTimer(next.jobUid, next.jobId);
    },
    [userId, memberUid, queryClient, pendingStart, beginTimer]
  );

  const discard = useCallback(async () => {
    if (!userId || !memberUid) return;
    await discardTimer(userId, memberUid);
    longRunningPromptedRef.current = false;
    setIsLogModalOpen(false);
    const next = pendingStart;
    setPendingStart(null);
    if (next) await beginTimer(next.jobUid, next.jobId);
  }, [userId, memberUid, pendingStart, beginTimer]);

  const pause = useCallback(async () => {
    if (!userId || !memberUid) return;
    await pauseTimer(userId, memberUid);
  }, [userId, memberUid]);

  const resume = useCallback(async () => {
    if (!userId || !memberUid) return;
    await resumeTimer(userId, memberUid);
  }, [userId, memberUid]);

  // Reminder loop: every 30 minutes of running time, nudge via a browser
  // notification (or the in-app toast when permission wasn't granted).
  // claimTimerReminder atomically advances lastNotifiedAt in Firestore, so of
  // all open tabs/devices only the one that wins the claim notifies.
  useEffect(() => {
    if (!userId || !memberUid || !myTimer) return;

    const check = () => {
      const now = Date.now();
      const startedMs = myTimer.startedAt.toMillis();
      // Ignore a pre-start lastNotifiedAt (stale leftover from a prior session).
      const referenceMs = Math.max(
        startedMs,
        myTimer.lastNotifiedAt?.toMillis() ?? startedMs
      );
      const elapsed = now - startedMs;

      // Paused: no work reminders, but nudge once if the pause itself is
      // forgotten, so "paused" doesn't become the new left-running timer.
      if (myTimer.pausedAt) {
        const pausedFor = now - myTimer.pausedAt.toMillis();
        if (pausedFor >= LONG_PAUSED_MS && !longPausedPromptedRef.current) {
          longPausedPromptedRef.current = true;
          setAlert({
            type: "warning",
            title: "Timer still paused",
            content: `Your timer on Job #${myTimer.jobId} has been paused for over 2 hours. Resume it, or stop it to log the time.`,
            persistent: true,
          });
        }
        return;
      }
      longPausedPromptedRef.current = false;

      if (now - referenceMs >= REMINDER_INTERVAL_MS) {
        claimTimerReminder(userId, memberUid, REMINDER_INTERVAL_MS)
          .then((won) => {
            if (!won) return;
            const body = `${formatElapsed(
              getWorkedMs(myTimer, now)
            )} on Job #${
              myTimer.jobId
            }. Still working? Stop the timer when you're done to log it.`;
            if (
              typeof Notification !== "undefined" &&
              Notification.permission === "granted"
            ) {
              const notification = new Notification("Timer running", { body });
              notification.onclick = () => {
                window.focus();
                setIsLogModalOpen(true);
              };
            } else {
              setAlert({ type: "info", title: "Timer running", content: body });
            }
          })
          .catch(() => {});
      }

      if (elapsed >= LONG_RUNNING_MS && !longRunningPromptedRef.current) {
        longRunningPromptedRef.current = true;
        setAlert({
          type: "warning",
          title: "Timer left running?",
          content: `Your timer on Job #${myTimer.jobId} has been going for over 12 hours. Stop it to log the time. You can adjust the start and end before saving.`,
          persistent: true,
        });
      }
    };

    check();
    const interval = setInterval(check, 60 * 1000);
    return () => clearInterval(interval);
  }, [userId, memberUid, myTimer, setAlert]);

  const value = useMemo(
    () => ({
      allTimers,
      myTimer,
      isLogModalOpen,
      openLogModal,
      closeLogModal,
      pendingStart,
      requestStart,
      stopAndLog,
      discard,
      pause,
      resume,
    }),
    [
      allTimers,
      myTimer,
      isLogModalOpen,
      openLogModal,
      closeLogModal,
      pendingStart,
      requestStart,
      stopAndLog,
      discard,
      pause,
      resume,
    ]
  );

  return (
    <TimeClockContext.Provider value={value}>
      {children}
    </TimeClockContext.Provider>
  );
};

export const useTimeClock = () => {
  const context = useContext(TimeClockContext);
  if (context === undefined) {
    throw new Error("useTimeClock must be used within a TimeClockProvider");
  }
  return context;
};
